<template>
  <div class="p-4">
    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица запросов -->
    <template v-else>
      <table v-if="requests.length > 0" class="w-full border-collapse bg-white shadow-md">
      <thead class="bg-gray-100">
        <tr>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">ID</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Тип</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Маршрут</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Статус</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Дата создания</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Действия</th>
        </tr>
      </thead>
      <tbody>
        <tr 
          v-for="request in requests" 
          :key="request.id" 
          class="border-b hover:bg-gray-50 cursor-pointer"
          @click="showModal(request)"
        >
          <td class="px-4 py-2 text-sm text-gray-700">{{ request.id }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ request.type }}</td>
          <td class="px-4 py-2 text-sm text-gray-700"><span class="break-all inline-block max-w-md">{{ decodeRoute(request.route) }}</span></td>
          <td class="px-4 py-2 text-sm">
            <span 
              :class="[
                'px-2 py-1 rounded-full text-xs font-medium',
                getStatusClass(request.status)
              ]"
            >
              {{ getStatusLabel(request.status) }}
            </span>
          </td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ formatDate(request.created_at) }}</td>
          <td class="px-4 py-2">
            <button
              @click.stop="showModal(request)"
              class="px-2 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Просмотр
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <Pagination
      v-if="pagination.lastPage > 1"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :per-page="pagination.perPage"
      @change-page="changePage"
    />
    <EmptyState v-else-if="requests.length === 0" />
    </template>

    <!-- Модальное окно с исходниками -->
    <div 
      v-if="selectedRequest" 
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeModal"
    >
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-4xl max-h-[90vh] flex flex-col">
        <div class="p-6 border-b flex justify-between items-center">
          <h2 class="text-lg font-bold">Детали запроса #{{ selectedRequest.id }}</h2>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="p-6 overflow-y-auto flex-1">
          <div class="mb-6">
            <h3 class="text-sm font-semibold text-gray-700 mb-2">Основная информация</h3>
            <div class="bg-gray-50 p-4 rounded-md space-y-2">
              <div class="flex">
                <span class="font-medium text-gray-600 w-32">Тип:</span>
                <span class="text-gray-800">{{ selectedRequest.type }}</span>
              </div>
              <div class="flex">
                <span class="font-medium text-gray-600 w-32">Маршрут:</span>
                <span class="text-gray-800 break-all">{{ decodeRoute(selectedRequest.route) }}</span>
              </div>
              <div class="flex">
                <span class="font-medium text-gray-600 w-32">Статус:</span>
                <span 
                  :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    getStatusClass(selectedRequest.status)
                  ]"
                >
                  {{ getStatusLabel(selectedRequest.status) }}
                </span>
              </div>
              <div class="flex">
                <span class="font-medium text-gray-600 w-32">Дата создания:</span>
                <span class="text-gray-800">{{ formatDate(selectedRequest.created_at) }}</span>
              </div>
            </div>
          </div>

          <div class="mb-6">
            <h3 class="text-sm font-semibold text-gray-700 mb-2">Запрос (Request)</h3>
            <pre class="bg-gray-900 text-green-400 p-4 rounded-md overflow-x-auto text-xs">{{ formatJson(selectedRequest.request) }}</pre>
          </div>

          <div v-if="selectedRequest.response">
            <h3 class="text-sm font-semibold text-gray-700 mb-2">Ответ (Response)</h3>
            <pre class="bg-gray-900 text-blue-400 p-4 rounded-md overflow-x-auto text-xs">{{ formatJson(selectedRequest.response) }}</pre>
          </div>
          <div v-else>
            <h3 class="text-sm font-semibold text-gray-700 mb-2">Ответ (Response)</h3>
            <div class="bg-gray-100 p-4 rounded-md text-gray-500 text-sm">
              Ответ еще не получен
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination.vue';
import EmptyState from '@/components/EmptyState.vue';
import { createUrlStateManager } from '@/utils/urlStateManager';

export default {
  components: { Pagination, EmptyState },
  data() {
    return {
      requests: [],
      loading: true,
      selectedRequest: null,
      page: 1,
      perPage: 20,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20,
      },
    };
  },
  created() {
    createUrlStateManager(this, {
      page: { type: 'number', default: 1, urlKey: 'page' },
    }, { routeName: 'OneCRequests' });

    this.getRequests();
  },
  methods: {
    decodeRoute(route) {
      if (!route) return '';
      try {
        return decodeURIComponent(route);
      } catch {
        return route;
      }
    },
    getRequests() {
      this.loading = true;
      axios
        .post('/api/onecRequest/getRequests', {
          page: this.page,
          per_page: this.perPage,
        })
        .then((response) => {
          const d = response.data.data;
          this.requests = d.data || [];
          this.pagination = {
            currentPage: d.current_page,
            lastPage: d.last_page,
            total: d.total,
            perPage: d.per_page,
          };
          this.loading = false;
        })
        .catch((error) => {
          console.error('Ошибка загрузки запросов:', error);
          this.loading = false;
        });
    },
    changePage(page) {
      this.page = page;
      this.getRequests();
    },
    showModal(request) {
      this.selectedRequest = request;
    },
    closeModal() {
      this.selectedRequest = null;
    },
    formatJson(data) {
      if (!data) return 'Нет данных';
      return JSON.stringify(data, null, 2);
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getStatusLabel(status) {
      const labels = {
        wait: 'Ожидание',
        done: 'Выполнено',
        failed: 'Ошибка',
      };
      return labels[status] || status;
    },
    getStatusClass(status) {
      const classes = {
        wait: 'bg-yellow-100 text-yellow-800',
        done: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800',
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },
  },
};
</script>
